import { useState, useEffect, useCallback, useRef } from 'react'
import { LocalStorage } from '../helpers/LocalStorage'
import {
    ChatWSMessage,
    DisplayChatMessage,
} from '../components/AdminPanel/Lori Manager/liveTypes'

const AUTH_ERROR_CODE = 4003
const NOT_FOUND_CODE = 4004

interface UseAdminChatReturn {
    messages: DisplayChatMessage[]
    takeoverActive: boolean
    takeoverAdminId: string | null
    iAmTakeover: boolean
    isConnected: boolean
    isLoading: boolean
    error: string | null
    sendTakeover: () => void
    sendRelease: () => void
    sendMessage: (text: string) => void
}

export const useAdminChat = (conversationId: number): UseAdminChatReturn => {
    const [messages, setMessages] = useState<DisplayChatMessage[]>([])
    const [takeoverActive, setTakeoverActive] = useState(false)
    const [takeoverAdminId, setTakeoverAdminId] = useState<string | null>(null)
    const [iAmTakeover, setIAmTakeover] = useState(false)
    const [isConnected, setIsConnected] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const socketRef = useRef<WebSocket | null>(null)
    const unmountedRef = useRef(false)

    useEffect(() => {
        unmountedRef.current = false

        const wsBaseUrl = process.env.REACT_APP_WEBSOCKET_BASE_URL
        if (!wsBaseUrl) {
            setError('WebSocket configuration error')
            setIsLoading(false)
            return
        }

        const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
        if (!token) {
            setError('Not authenticated')
            setIsLoading(false)
            return
        }

        const url = `${wsBaseUrl}admin/chat/${conversationId}/?token=${token}`
        const ws = new WebSocket(url)

        ws.onopen = () => {
            if (unmountedRef.current) {
                ws.close(1000)
                return
            }
            setIsConnected(true)
        }

        ws.onmessage = (event) => {
            const data: ChatWSMessage = JSON.parse(event.data)

            switch (data.type) {
                case 'conversation_history':
                    setMessages(
                        data.messages.map((msg) => ({
                            role: msg.role,
                            message: msg.message,
                            sender:
                                msg.agent_name === 'human_admin'
                                    ? 'admin'
                                    : msg.role === 'user'
                                    ? 'user'
                                    : 'lori',
                            agentName: msg.agent_name,
                            adminUserId: '',
                            createdDate: msg.created_date,
                        })),
                    )
                    setIsLoading(false)
                    break

                case 'status':
                    setTakeoverActive(data.takeover_active)
                    setTakeoverAdminId(data.takeover_admin_id)
                    break

                case 'chat_message':
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: data.role,
                            message: data.message,
                            sender: data.sender,
                            agentName: null,
                            adminUserId: data.admin_user_id,
                            createdDate: data.created_date,
                        },
                    ])
                    break

                case 'takeover_confirmed':
                    setIAmTakeover(true)
                    setTakeoverActive(true)
                    break

                case 'takeover_notification':
                    setTakeoverActive(true)
                    setTakeoverAdminId(data.admin_user_id)
                    break

                case 'release_confirmed':
                    setIAmTakeover(false)
                    setTakeoverActive(false)
                    setTakeoverAdminId(null)
                    break

                case 'release_notification':
                    setTakeoverActive(false)
                    setTakeoverAdminId(null)
                    break

                case 'error':
                    setError(data.message)
                    break
            }
        }

        ws.onclose = (event) => {
            setIsConnected(false)
            setIsLoading(false)
            socketRef.current = null

            if (event.code === AUTH_ERROR_CODE) {
                setError('Not authorized')
            } else if (event.code === NOT_FOUND_CODE) {
                setError('Conversation not found')
            } else if (event.code !== 1000) {
                setError((prev) => prev ?? 'Connection closed unexpectedly')
            }
        }

        ws.onerror = () => {
            setError('Connection error')
            setIsLoading(false)
        }

        socketRef.current = ws

        return () => {
            unmountedRef.current = true
            if (
                ws.readyState === WebSocket.OPEN ||
                ws.readyState === WebSocket.CONNECTING
            ) {
                ws.close(1000)
            }
            socketRef.current = null
        }
    }, [conversationId])

    const sendTakeover = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: 'takeover' }))
        }
    }, [])

    const sendRelease = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: 'release' }))
        }
    }, [])

    const sendMessage = useCallback((text: string) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(
                JSON.stringify({ type: 'message', message: text }),
            )
        }
    }, [])

    return {
        messages,
        takeoverActive,
        takeoverAdminId,
        iAmTakeover,
        isConnected,
        isLoading,
        error,
        sendTakeover,
        sendRelease,
        sendMessage,
    }
}
